import * as _ from 'lodash-es';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import type { QueryResult } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import useIsPublicView from '../utils/useIsPublicView';

// eslint-disable-next-line slab-graphql/no-unused-fields
const USER_FRAGMENT = gql(`
  fragment useAllUsers_user on User {
    id
    name
    email
    title
    description
    type
    deactivatedAt
    importedFrom
    avatar {
      thumb
    }
  }
`);

export type FullUser = DocumentType<typeof USER_FRAGMENT>;

export const ALL_USERS_QUERY = gql(/** @complexity 10 */ `
  query allUsers($includeDeactivated: Boolean) {
    session: currentSession {
      organization {
        id
        users(includeDeactivated: $includeDeactivated) {
          id
          ...useAllUsers_user
        }
      }
    }
  }
`);

const USER_CREATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription userCreated {
    user: userCreated {
      ...useAllUsers_user
    }
  }
`);

const USER_UPDATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription userUpdated {
    user: userUpdated {
      ...useAllUsers_user
    }
  }
`);

const useSubscribeEffect = (
  subscribeToMore: QueryResult<
    DocumentType<typeof ALL_USERS_QUERY>,
    VariablesOf<typeof ALL_USERS_QUERY>
  >['subscribeToMore'],
  skip: boolean,
) => {
  useEffect(() => {
    if (skip) return undefined;

    const unsubscribes = [
      USER_CREATED_SUBSCRIPTION,
      USER_UPDATED_SUBSCRIPTION,
    ].map((query) => {
      const path = 'session.organization.users';
      return subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(path, 'user', true),
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [skip, subscribeToMore]);
};

interface UseAllUsersQueryOptions {
  skip?: boolean;
}

const useAllUsersQuery = (options?: UseAllUsersQueryOptions) => {
  const response = useQuery(ALL_USERS_QUERY, {
    variables: {
      includeDeactivated: true,
    },
    skip: options?.skip,
  });
  const { data } = response;

  useSubscribeEffect(response.subscribeToMore, options?.skip ?? false);

  return useMemo(() => _.compact(data?.session?.organization?.users), [data]);
};

const AllUsersContext = createContext<{
  users: ReturnType<typeof useAllUsersQuery>;
  usersWithoutDeactivated: ReturnType<typeof useAllUsersQuery>;
  userMap: Record<string, ReturnType<typeof useAllUsersQuery>[number]>;
  userMapWithoutDeactivated: Record<
    string,
    ReturnType<typeof useAllUsersQuery>[number]
  >;
}>({
  users: [],
  usersWithoutDeactivated: [],
  userMap: {},
  userMapWithoutDeactivated: {},
});

interface UseAllUsersOptions {
  includeDeactivated?: boolean;
}

interface AllUsersProviderProps {
  children: React.ReactNode;
}

export const AllUsersProvider = ({ children }: AllUsersProviderProps) => {
  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();
  const skip = !isNonVisitorUser || isPublicView;

  const users = useAllUsersQuery({ skip });

  const value = useMemo(() => {
    const usersWithoutDeactivated = users.filter((user) => !user.deactivatedAt);

    return {
      users,
      usersWithoutDeactivated,
      userMap: _.keyBy(users, 'id'),
      userMapWithoutDeactivated: _.keyBy(usersWithoutDeactivated, 'id'),
    };
  }, [users]);

  return (
    <AllUsersContext.Provider value={value}>
      {children}
    </AllUsersContext.Provider>
  );
};

const useAllUsers = (options?: UseAllUsersOptions) => {
  const { users, usersWithoutDeactivated } = useContext(AllUsersContext);

  return useMemo(
    () => (options?.includeDeactivated ? users : usersWithoutDeactivated),
    [options?.includeDeactivated, users, usersWithoutDeactivated],
  );
};

export const useAllAdmins = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Admin);
};

export const useAllBots = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Bot);
};

export const useAllUsersMap = (options?: UseAllUsersOptions) => {
  const { userMap, userMapWithoutDeactivated } = useContext(AllUsersContext);

  return useMemo(
    () => (options?.includeDeactivated ? userMap : userMapWithoutDeactivated),
    [options?.includeDeactivated, userMap, userMapWithoutDeactivated],
  );
};

// using a fragment on User is to avoid that useUser is
// used with UserFields, which currently can't be cached
const GET_USER_FRAGMENT = gql(`
  fragment useGetUser_user on User {
    id
  }
`);

const GET_USER_SLIM_FRAGMENT = gql(`
  fragment useGetUser_slimUser on SlimUser {
    id
  }
`);

export function useGetUser() {
  const userMap = useAllUsersMap({ includeDeactivated: true });

  return useCallback(
    (
      user:
        | DocumentType<typeof GET_USER_FRAGMENT>
        | DocumentType<typeof GET_USER_SLIM_FRAGMENT>,
    ): FullUser => {
      const { id } = user;

      const defaultUser: FullUser = {
        id,
        name: 'Anonymous',
        email: 'anonymous@slab.com',
        description: '',
        title: '',
        type: UserType.User,
      };

      return userMap[user.id] ?? defaultUser;
    },
    [userMap],
  );
}

export default useAllUsers;
